import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { openingHours } from '@/mocks/hours';

const quickLinks = [
  { path: '/', label: 'nav.home' },
  { path: '/soins', label: 'nav.services' },
  { path: '/galerie', label: 'nav.gallery' },
  { path: '/univers', label: 'nav.about' },
  { path: '/temoignages', label: 'nav.testimonials' },
  { path: '/contact', label: 'nav.contact' },
  { path: '/rdv', label: 'nav.book' },
];

export default function Footer() {
  const { t } = useTranslation();

  return (
    <footer className="bg-foreground-950 pt-16 md:pt-20 pb-6">
      <div className="w-full px-4 md:px-8 lg:px-12">
        {/* Top row: 4 columns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8 mb-14">

          {/* Brand — lg:col-span-4 */}
          <div className="lg:col-span-4">
            <Link to="/" className="flex items-center gap-3 mb-5">
              <img
                src="https://static.readdy.ai/image/66108e401cf688a02511c33e687ef000/027bb56ccb9ee3af4b09b48629b253d4.png"
                alt="KD'S BEAUTY"
                className="h-10 w-auto object-contain"
              />
              <span className="font-heading text-xl text-background-50 tracking-wide">
                KD'S BEAUTY
              </span>
            </Link>
            <p className="text-sm text-foreground-400 leading-relaxed mb-6 max-w-xs">
              {t('footer.about.desc')}
            </p>
            {/* Social icons */}
            <div className="flex items-center gap-3">
              <a href="https://tiktok.com" target="_blank" rel="noopener noreferrer nofollow"
                className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-50/8 text-foreground-400 hover:bg-background-50 hover:text-foreground-950 transition-all duration-300"
                aria-label="TikTok"
              >
                <i className="ri-tiktok-fill text-base" />
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer nofollow"
                className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-50/8 text-foreground-400 hover:bg-background-50 hover:text-foreground-950 transition-all duration-300"
                aria-label="Instagram"
              >
                <i className="ri-instagram-line text-base" />
              </a>
              <a href="https://wa.me/2250757006969" target="_blank" rel="noopener noreferrer nofollow"
                className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-50/8 text-foreground-400 hover:bg-green-500 hover:text-background-50 transition-all duration-300"
                aria-label="WhatsApp"
              >
                <i className="ri-whatsapp-line text-base" />
              </a>
            </div>
          </div>

          {/* Quick Links — lg:col-span-2 */}
          <div className="lg:col-span-2">
            <h4 className="font-label text-xs text-background-50/50 uppercase tracking-[0.2em] mb-5">
              {t('footer.quickLinks')}
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-foreground-400 hover:text-background-50 transition-colors duration-200"
                  >
                    {t(link.label)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Hours — lg:col-span-3 */}
          <div className="lg:col-span-3">
            <h4 className="font-label text-xs text-background-50/50 uppercase tracking-[0.2em] mb-5">
              {t('footer.hours')}
            </h4>
            <ul className="space-y-2.5">
              {openingHours.map((item) => (
                <li key={item.day} className="flex justify-between text-sm">
                  <span className="text-foreground-400">{item.day}</span>
                  <span className={`font-medium ${item.hours === 'Fermé' ? 'text-accent-400' : 'text-background-50/80'}`}>
                    {item.hours}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact — lg:col-span-3 */}
          <div className="lg:col-span-3">
            <h4 className="font-label text-xs text-background-50/50 uppercase tracking-[0.2em] mb-5">
              {t('footer.contact')}
            </h4>
            <ul className="space-y-3.5">
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                  <i className="ri-map-pin-line text-sm text-primary-400" />
                </div>
                <div className="text-sm text-foreground-400 leading-relaxed">
                  <p>{t('contact.address')}</p>
                  <p>{t('contact.city')}</p>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-5 h-5 flex items-center justify-center shrink-0">
                  <i className="ri-phone-line text-sm text-primary-400" />
                </div>
                <a href="tel:+2250757006969" className="text-sm text-foreground-400 hover:text-background-50 transition-colors">
                  +225 07 57 00 69 69
                </a>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-5 h-5 flex items-center justify-center shrink-0">
                  <i className="ri-whatsapp-line text-sm text-green-500" />
                </div>
                <a href="https://wa.me/2250757006969" target="_blank" rel="noopener noreferrer nofollow" className="text-sm text-foreground-400 hover:text-green-400 transition-colors">
                  WhatsApp
                </a>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-5 h-5 flex items-center justify-center shrink-0">
                  <i className="ri-mail-line text-sm text-primary-400" />
                </div>
                <a href={`mailto:${t('contact.email')}`} className="text-sm text-foreground-400 hover:text-background-50 transition-colors">
                  {t('contact.email')}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-background-50/8 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-foreground-500">
            {t('footer.copyright', { year: new Date().getFullYear() })}
          </p>
          <div className="flex items-center gap-5">
            <span className="text-xs text-foreground-500 hover:text-background-50/70 transition-colors cursor-pointer">
              {t('footer.legal')}
            </span>
            <span className="text-xs text-foreground-500 hover:text-background-50/70 transition-colors cursor-pointer">
              {t('footer.privacy')}
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}